"use client";

import { createColumnHelper } from "@tanstack/react-table";
import { ProductParamsItem } from "./types";
import { useEffect, useMemo, useState } from "react";
import ProductDisplay from "@/components/ProductFilter/ProductDisplay";
import DisplayImage from "@/components/ProductFilter/DisplayImage";
import { ParamsItem } from "@/components/ProductFilter/type";
import ProductFilter from "@/components/ProductFilter";
import useSWR from "swr";
import { fetchCollectionsData } from "@/lib/fetchData";

interface DirectusFile {
  id: string;
  title: string;
  filename_download: string;
}

async function getProductsData(params: {
  site: string | null;
  lang: string | null;
  categoryId: number;
  params: ProductParamsItem[];
}) {
  const data = await fetchCollectionsData<{
    slug: string;
    material_number: string;
    params_data: ParamsItem[];
    images: {
      directus_files_id: string;
    }[];
    description: string;
    publish_date: string;
    is_new_product?: boolean;
    datasheet1_file?: DirectusFile;
    datasheet2_file?: DirectusFile;
    datasheet3_file?: DirectusFile;
    package_image: string;
    publish: {
      publish_id: {
        publish_date: string;
      };
    }[];
  }>("product", {
    site: params.site,
    lang: params.lang,
    fields: [
      "params_data",
      "images",
      "material_number",
      "is_new_product",
      "slug",
      "datasheet1_file.id",
      "datasheet1_file.title",
      "datasheet1_file.filename_download",
      "datasheet2_file.id",
      "datasheet2_file.title",
      "datasheet2_file.filename_download",
      "datasheet3_file.id",
      "datasheet3_file.title",
      "datasheet3_file.filename_download",
      "package_image",
      "publish.publish_id.publish_date",
    ],
    page: 1,
    excludePublishFields: true,
    limit: -1,
    sort: ["-is_new_product", "-publish.publish_id.publish_date"],
    querys: [
      {
        key: "category",
        value: params.categoryId.toString(),
      },
    ],
    tags: ["product"],
  });

  return data?.data.map((item) => {
    return {
      images: item.package_image ? [item.package_image] : [],
      material_number: item.material_number,
      is_new_product: item.is_new_product,
      slug: item.slug,
      datasheet1_file: item.datasheet1_file,
      datasheet2_file: item.datasheet2_file,
      datasheet3_file: item.datasheet3_file,
      ...item?.params_data?.reduce<Record<string, any>>?.((acc, cur) => {
        let key = params?.params?.find((param) => param.id === cur?.id)?.id;

        if (!key) {
          key = params?.params?.find((param) => param?.name === cur?.name)?.id;
        }

        acc[key ?? ""!] = cur;
        return acc;
      }, {}),
    };
  });
}

const columnHelper = createColumnHelper<Record<string, any>>();

export default function ProductSelectorClient(props: {
  site: string | null;
  lang: string | null;
  categoryId: number;
  categoryslug: string;
  params?: ProductParamsItem[];
  defaultSort: {
    id: string;
    desc: boolean;
  }[];
}) {

  console.log(props, 'props');
  

  const [isMobile, setIsMobile] = useState<boolean>();

  const { data, isLoading } = useSWR(
    ["/api/products", props.categoryId],
    async () => {
      const res = await getProductsData({
        site: props.site,
        lang: props.lang,
        categoryId: props.categoryId,
        params: props.params ?? [],
      });

      return res;
    }
  );

  console.log(data, 'data');

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("material_number", {
        header: "Product number",
        size: isMobile ? 140 : 250,
        minSize: isMobile ? 140 : 250,
        enableSorting: false,
        // @ts-ignore
        filterFn: "product",
        cell: (info) => {
          const data = info.row.original as {
            slug: string;
            material_number: string;
            is_new_product: boolean;
            publish_date: string | null;
            datasheet1_file: DirectusFile;
            datasheet2_file: DirectusFile;
            datasheet3_file: DirectusFile;
          };

          return <ProductDisplay data={data} row={info.row} />;
        },
      }),
      columnHelper.accessor("images", {
        header: "Images",
        size: 90,
        minSize: 90,
        maxSize: 90,
        enableResizing: false,
        enableSorting: false,
        cell: (info) => {
          const material_number = info.row.original.material_number;

          const images = info.getValue();

          return (
            <DisplayImage images={images} material_number={material_number} />
          );
        },
        enableColumnFilter: false,
      }),
      ...(props.params ?? []).map?.((param) => {
        return columnHelper.accessor(param.id.toString(), {
          header: param.name,
          // @ts-ignore
          filterFn: param.type,
          enableSorting: param.type !== "text" && param.type !== "range",
          // @ts-ignore
          sortingFn: param.type,
          sortDescFirst: false,

          enableMultiSort: true,

          optionsId:
            param.type === "select" || param.type === "selects"
              ? param.id
              : null,

          cell: (info) => {
            const data = info.getValue() as ParamsItem;

            if (data?.data?.display) {
              if (param.type !== "text") {
                const arrText = data.data.display.split(",");

                return (
                  <>
                    {arrText.length === 1
                      ? arrText?.[0]
                      : arrText.map((v, i) => (
                          <div key={i} className="break-words leading-normal">
                            {v}
                            {i === arrText.length - 1 ? "" : ","}
                          </div>
                        ))}
                  </>
                );
              }

              return data.data.display;
            }

            return "-";
          },
        });
      }),
    ];
  }, [isMobile, props.params]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const buttonEl = document.getElementById("view-all-product-button");

      const productSelector = document.getElementById("product-selector");

      if (buttonEl) {
        buttonEl.addEventListener("click", () => {
          productSelector?.scrollIntoView({ behavior: "smooth" });
        });
      }
    }
  }, []);

  useEffect(() => {
    const listener = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  return (
    <ProductFilter
      defaultSort={props.defaultSort}
      count={data?.length ?? 0}
      data={data ?? []}
      loading={isLoading}
      columns={columns ?? []}
      categorySlug={props.categoryslug}
    />
  );
}
